import { Box, Checkbox, Divider, Flex, Input, InputGroup, InputRightElement, Text } from '@chakra-ui/react'
import { memo, useMemo, useRef, useState } from 'react'
import { useBrands } from '../../../hooks/useOrganization'
import { capitalizeWords } from '../../../utils/functions/formatters'
import LazyLoadAvatar from '../media/LazyLoadedAvatar'

interface Brand {
  id: string
  name: string
  logo: string | null | undefined
}

interface FormBrandMultiSelectProps {
  value: Brand[] | undefined
  onChange: (brands: Brand[]) => void
  onRemove?: (brand: Brand) => void
  isDisabled?: boolean
  name: string
  placeholder?: string
  height?: string
  maxHeight?: string
  memberId?: string
  // Let the parent own the label (e.g. when a mode toggle sits beside it).
  hideLabel?: boolean
}

const FormBrandMultiSelect = ({
  value,
  onChange,
  onRemove,
  isDisabled,
  name,
  placeholder,
  height = '40px',
  maxHeight = '200px',
  memberId,
  hideLabel,
}: FormBrandMultiSelectProps) => {
  const [brandSearch, setBrandSearch] = useState('')
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const brands = useBrands(
    { page: 1, search: brandSearch, page_size: 15, fields: 'id,name,logo', member: memberId },
    brandSearch.length > 1,
  )

  // Filter and sort brands - selected brands first (alphabetically), then unselected (alphabetically)
  // Selected brands always appear at top even if they don't match search
  const filteredBrands = useMemo(() => {
    const currentValue = value || []
    const selectedIds = new Set(currentValue.map((b) => b.id))
    const searchTerm = brandSearch.toLowerCase()

    // Always include selected brands, even if they don't match search
    const selected = currentValue
      .map((brand) => ({
        id: brand.id,
        name: brand.name,
        logo: brand.logo,
      }))
      .sort((a, b) => a.name.localeCompare(b.name))

    // Filter unselected brands based on search
    const unselected =
      brands.data?.data
        ?.filter((brand: Brand) => {
          return !selectedIds.has(brand.id) && brand.name.toLowerCase().includes(searchTerm)
        })
        .map((brand: Brand) => ({
          id: brand.id,
          name: brand.name,
          logo: brand.logo,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)) || []

    return [...selected, ...unselected]
  }, [brands.data?.data, brandSearch, value])

  const handleToggleBrand = (brand: Brand) => {
    const currentValue = value || []
    const isSelected = currentValue.some((selected) => selected?.id === brand?.id)

    if (isSelected) {
      const newValue = currentValue.filter((b) => b.id !== brand.id)
      onChange(newValue)
      if (onRemove) {
        onRemove(brand)
      }
    } else {
      onChange([...currentValue, brand])
    }

    // Keep the input focused after selection
    setTimeout(() => {
      inputRef.current?.focus()
    }, 0)
  }

  const handleSearchChange = (searchValue: string) => {
    setBrandSearch(searchValue)
    setIsDropdownOpen(searchValue.length > 0)
  }

  const handleInputFocus = () => {
    // Open dropdown if there's search text OR if there are selected brands
    if (brandSearch.length > 0 || (value?.length ?? 0) > 0) {
      setIsDropdownOpen(true)
    }
  }

  const handleInputBlur = () => {
    // Use a timeout to allow click events to process first
    setTimeout(() => {
      // Check if the new focused element is inside our container
      if (containerRef.current && !containerRef.current.contains(document.activeElement)) {
        setIsDropdownOpen(false)
        setBrandSearch('') // Clear the search text on blur
      }
    }, 150)
  }

  const handleDropdownMouseDown = (e: React.MouseEvent) => {
    // Prevent the input from losing focus when clicking on dropdown items
    e.preventDefault()
  }

  const isBrandSelected = (brand: Brand) => {
    const currentValue = value || []
    return currentValue.some((selected) => selected?.id === brand?.id)
  }

  const handleSelectAll = () => {
    const currentValue = value || []
    const existingIds = new Set(currentValue.map((b) => b.id))
    const toAdd = filteredBrands.filter((brand) => !existingIds.has(brand.id))
    if (toAdd.length === 0) return
    onChange([...currentValue, ...toAdd])
    setTimeout(() => {
      inputRef.current?.focus()
    }, 0)
  }

  const handleUnselectAll = () => {
    if ((value?.length ?? 0) === 0) return
    if (onRemove) {
      ;(value || []).forEach((brand) => onRemove(brand))
    }
    onChange([])
    setTimeout(() => {
      inputRef.current?.focus()
    }, 0)
  }

  const areAllSelected =
    filteredBrands.length > 0 && filteredBrands.every((brand) => isBrandSelected(brand))

  // The dropdown is absolutely positioned below the input, which itself sits
  // below the label. With `hideLabel` there is no label, so drop that offset —
  // otherwise the menu floats 36px too low (CORE-2817).
  const labelOffset = hideLabel ? '0px' : '36px'

  return (
    <Box position="relative" w="100%" ref={containerRef}>
      {!hideLabel && (
        <Text color="neutral.900" fontWeight="400" textStyle="b-sm" mb="8px">
          {capitalizeWords(name)}
        </Text>
      )}

      {/* Search input */}
      <InputGroup>
        <Input
          ref={inputRef}
          value={brandSearch}
          onChange={(e) => handleSearchChange(e.target.value)}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          h={{ base: '44px', md: height }}
          aria-label={placeholder || `Search for ${name.toLowerCase()}`}
          placeholder={placeholder || `Search for ${name.toLowerCase()}`}
          fontSize={{ base: '16px', md: '12px' }}
          disabled={isDisabled}
        />
        {(value?.length ?? 0) > 0 && (
          <InputRightElement h={{ base: '44px', md: height }} pr="12px">
            <Flex
              bg="primary.500"
              borderRadius="full"
              h="24px"
              minW="24px"
              px="8px"
              alignItems="center"
              justifyContent="center"
            >
              <Text fontSize="12px" fontWeight="600" color="white">
                {value?.length ?? 0}
              </Text>
            </Flex>
          </InputRightElement>
        )}
      </InputGroup>

      {/* Dropdown with search results */}
      {isDropdownOpen && !isDisabled && (
        <Box
          w="100%"
          pos="absolute"
          zIndex="1000"
          top={{
            base: `calc(44px + ${labelOffset})`,
            md: `calc(${height} + ${labelOffset})`,
          }}
          overflow="hidden"
          bg="white"
          py="6px"
          boxShadow="0px 6px 35px 5px #0000001F"
          borderColor="neutral.100"
          borderWidth="1px"
          borderRadius="8px"
          maxHeight={maxHeight}
          overflowY="auto"
          onMouseDown={handleDropdownMouseDown}
        >
          {!brands.isFetching && filteredBrands.length > 0 && (
            <>
              <Flex px="16px" py="8px" alignItems="center" justifyContent="space-between" gap="12px">
                <Text
                  as="button"
                  type="button"
                  fontSize="12px"
                  fontWeight="600"
                  color={areAllSelected ? 'neutral.300' : 'primary.500'}
                  cursor={areAllSelected ? 'not-allowed' : 'pointer'}
                  _hover={areAllSelected ? undefined : { color: 'primary.600' }}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (!areAllSelected) handleSelectAll()
                  }}
                >
                  Select all
                </Text>
                <Text
                  as="button"
                  type="button"
                  fontSize="12px"
                  fontWeight="600"
                  color={(value?.length ?? 0) === 0 ? 'neutral.300' : 'primary.500'}
                  cursor={(value?.length ?? 0) === 0 ? 'not-allowed' : 'pointer'}
                  _hover={(value?.length ?? 0) === 0 ? undefined : { color: 'primary.600' }}
                  onClick={(e) => {
                    e.stopPropagation()
                    if ((value?.length ?? 0) > 0) handleUnselectAll()
                  }}
                >
                  Unselect all
                </Text>
              </Flex>
              <Divider m="0" />
            </>
          )}

          {brands.isFetching && (
            <Flex h="64px" p="2px 16px" alignItems="center" justifyContent="center">
              <Text w="100%" textAlign="center" textStyle="b-sm" color="neutral.500">
                Loading...
              </Text>
            </Flex>
          )}

          {!brands.isFetching && filteredBrands.length === 0 && (
            <Flex h="64px" p="2px 16px" alignItems="center">
              <Text w="100%" textAlign="center" textStyle="b-sm" color="neutral.500">
                No brands found
              </Text>
            </Flex>
          )}

          {!brands.isFetching &&
            filteredBrands.map((brand, i) => (
              <Box key={brand.id}>
                <Flex
                  px="16px"
                  h="48px"
                  cursor="pointer"
                  alignItems="center"
                  transition="0.2s ease-out"
                  justifyContent="flex-start"
                  py="8px"
                  _hover={{ bg: 'neutral.50' }}
                  onClick={() => handleToggleBrand(brand)}
                >
                  <Checkbox
                    isChecked={isBrandSelected(brand)}
                    onChange={() => handleToggleBrand(brand)}
                    size="md"
                    colorScheme="primary"
                    mr="12px"
                    pointerEvents="none"
                  />
                  <LazyLoadAvatar src={brand.logo} w={24} h={24} name={brand.name} />
                  <Box ml="8px">
                    <Text noOfLines={1} textStyle="b-md" color="neutral.900">
                      {brand.name}
                    </Text>
                  </Box>
                </Flex>
                {i !== filteredBrands.length - 1 && <Divider m="0" />}
              </Box>
            ))}
        </Box>
      )}
    </Box>
  )
}

// Custom isEqual function for memo optimization
const isEqual = (prevProps: Readonly<FormBrandMultiSelectProps>, nextProps: Readonly<FormBrandMultiSelectProps>) => {
  return (
    JSON.stringify(prevProps.value) === JSON.stringify(nextProps.value) &&
    prevProps.isDisabled === nextProps.isDisabled &&
    prevProps.name === nextProps.name &&
    prevProps.placeholder === nextProps.placeholder &&
    prevProps.memberId === nextProps.memberId &&
    prevProps.hideLabel === nextProps.hideLabel
  )
}

export default memo(FormBrandMultiSelect, isEqual)
